import { HTMLAttributes, memo, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Button, Input, message, Select, Tabs } from 'antd';
import LoadMore from 'components/LoadMore';
import Empty from 'components/Empty';
import { PlusOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/es/form';
import {
  useVoucherDetailShow,
  useSetVoucherDetailShow,
  useVoucherDetailData,
} from 'hooks/business/voucherDetail';
import { useMemoizedFn, useVirtualList } from 'ahooks';
import VoucherComponent from './VoucherComponent';
import useVoucher from './useVoucher';
import VoucherDrawer from './VoucherDrawer';
import useVoucherValues from './VoucherDrawer/useVoucherValues';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useVoucherOperate } from 'hooks/business/useVoucherOperate';
import { useVoucherSearchOptions } from 'hooks/business/chat/useChatSide';
import { VoucherSync } from './VoucherSync';
import { getCouponStatusLabel } from 'services/i18n/enum/coupon';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .${({ theme }) => theme.getAntdPrefixedClassName('tabs')} {
    &-nav::before {
      display: none;
    }
    &-ink-bar {
      display: none;
    }
  }
`;

const Content = styled.div`
  flex: 1 0 0;
  position: relative;
  overflow: auto;
  background: var(--fett-bg-subtle);
`;

const ButtonWrapper = styled.div`
  position: sticky;
  top: 0;
  padding: 12px 0;
  background: var(--fett-bg-subtle);
  z-index: 1;
`;

const List = styled.ul`
  padding: 0;
  list-style: none;
  > li {
    margin-top: 8px;
    margin-left: 12px;
    margin-right: 12px;
  }
`;

const SearchWrap = styled.div`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 4px;

  .ant-input-search-button {
    padding: 0 8px !important;
  }
`;

export interface VoucherProps extends HTMLAttributes<HTMLDivElement> {
  conversation: Conversation.Conversation;
}

function Voucher(props: VoucherProps) {
  const { conversation, ...otherProps } = props;
  const { channel } = conversation;
  const wrapperRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchOptions = useVoucherSearchOptions(channel);

  const { list, setList, fetchList, updateVoucher, lastSyncTime } = useVoucher();

  const idHeightMapRef = useRef<Record<string, number>>({});

  const [vList, scrollTo] = useVirtualList(
    useMemo(() => list ?? [], [list]),
    {
      containerTarget: containerRef,
      wrapperTarget: wrapperRef,
      overscan: 3,
      itemHeight: (_, { internalVoucherId }) => idHeightMapRef.current[internalVoucherId] || 198,
    },
  );

  const voucherDetailShow = useVoucherDetailShow();

  const voucherDetailData = useVoucherDetailData();

  const [hasMore, setHasMore] = useState(true);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { clearVoucherFormCache } = useVoucherValues();

  const [status, setStatus] = useState<Conversation.Voucher.Status>('ONGOING');

  useEffect(() => {
    scrollTo(0);
  }, [status, hasMore, scrollTo]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchKeywordType, setSearchKeywordType] =
    useState<Conversation.Voucher.SearchKeywordType>(0);

  const { t } = useTranslate();

  const [voucherDrawerTitle, setVoucherDrawerTitle] = useState<string>('');
  const [voucherDrawerConfirmText, setVoucherDrawerConfirmText] = useState<string>('');

  const voucherRef = useRef<{
    form: FormInstance;
    setFormValues: (voucher: Conversation.Voucher.Vo) => void;
    setStatus: (status: Conversation.Voucher.Status | undefined) => void;
  }>();

  const { canCreate } = useVoucherOperate(undefined, conversation);
  const isTiktok = isTiKTokUpperCase(channel);
  // Jolect 平台暂不支持创建优惠券
  const showCreateButton = canCreate && channel !== 'JOLECT';

  const editVoucher = useCallback(
    (voucher: Conversation.Voucher.Vo) => {
      voucherRef.current?.setFormValues(voucher);
      voucherRef.current?.setStatus(voucher.voucherStatus as Conversation.Voucher.Status);
      if (voucherDetailShow) {
        setVoucherDrawerTitle(t('common.action.view'));
      } else {
        setVoucherDrawerTitle(t('scene.chatWorkspace.couponForm.title.edit'));
      }
      setVoucherDrawerConfirmText(t('scene.chatWorkspace.couponForm.action.confirmEdit'));
      setDrawerVisible(true);
    },
    [t, voucherDetailShow],
  );

  const copyAndCreateVoucher = (voucher: Conversation.Voucher.Vo) => {
    const {
      voucherCode,
      internalVoucherId,
      externalVoucherId,
      startTime,
      endTime,
      displayCollectStartTime,
      ...otherProps
    } = voucher;
    voucherRef.current?.setFormValues(otherProps as Conversation.Voucher.Vo);
    voucherRef.current?.setStatus(undefined);
    // 清除缓存数据
    clearVoucherFormCache(storeId);
    setVoucherDrawerTitle(t('scene.chatWorkspace.couponForm.title.create'));
    setVoucherDrawerConfirmText(t('common.action.confirm'));
    setDrawerVisible(true);
  };

  useEffect(() => {
    if (voucherDetailShow && voucherDetailData) {
      editVoucher(voucherDetailData);
    }
  }, [voucherDetailShow, voucherDetailData, editVoucher]);

  const setVoucherDetailShow = useSetVoucherDetailShow();

  // 搜索优惠券
  const handleSearch = useMemoizedFn(() => {
    setList([]);
    setHasMore(true);
  });

  if (conversation === undefined) {
    return null;
  }

  const { storeId } = conversation;
  const syncNode = (
    <VoucherSync
      storeId={storeId}
      channel={channel}
      lastSyncTime={lastSyncTime}
      onSuccess={() => {
        setList([]);
        setHasMore(true);
      }}
    />
  );

  const selectBefore = (
    <Select
      className="max-w-[150px]"
      dropdownMatchSelectWidth={false}
      value={searchKeywordType}
      onChange={(val) => {
        setSearchKeywordType(val);
        if (searchKeyword) {
          handleSearch();
        }
      }}
    >
      {searchOptions.map((option) => {
        return (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        );
      })}
    </Select>
  );

  return (
    <Container {...otherProps}>
      <SearchWrap>
        <Input.Search
          allowClear
          enterButton
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          placeholder={t('common.placeholder.search')}
          addonBefore={selectBefore}
          onSearch={handleSearch}
        />
        {isTiktok && <div className="mr-[-4px]">{syncNode}</div>}
      </SearchWrap>
      {!isTiktok && (
        <Tabs
          size="small"
          activeKey={status}
          onChange={(key) => {
            setStatus(key as Conversation.Voucher.Status);
            setList([]);
            setHasMore(true);
          }}
          tabBarExtraContent={<div className="mr-[4px]">{syncNode}</div>}
        >
          {['ALL', 'ONGOING', 'SCHEDULED', 'ENDED'].map((status) => {
            return (
              <Tabs.TabPane
                key={status}
                tab={getCouponStatusLabel(t, status as 'ALL' | 'ONGOING' | 'SCHEDULED' | 'ENDED')}
              />
            );
          })}
        </Tabs>
      )}
      <Content>
        {showCreateButton && (
          <ButtonWrapper>
            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              onClick={() => {
                voucherRef.current?.form.resetFields();
                voucherRef.current?.setStatus(undefined);
                setVoucherDrawerTitle(t('scene.chatWorkspace.couponForm.title.create'));
                setVoucherDrawerConfirmText(t('common.action.confirm'));
                setDrawerVisible(true);
              }}
            >
              {t('scene.chatWorkspace.couponTab.action.createCoupon')}
            </Button>
          </ButtonWrapper>
        )}
        {!hasMore && list?.length === 0 ? (
          <Empty
            description={
              searchKeyword ? (
                <>
                  <div>{t('scene.chatWorkspace.couponTab.empty.noMatchedCoupon')}</div>
                  <div>{t('scene.chatWorkspace.couponTab.empty.searchHint')}</div>
                </>
              ) : (
                t('scene.chatWorkspace.couponTab.empty')
              )
            }
          />
        ) : (
          <div
            ref={containerRef}
            style={{
              overflow: 'scroll',
              height: `calc(100% - ${showCreateButton ? '56' : '0'}px)`,
            }}
          >
            <LoadMore
              key={status}
              hasMore={hasMore}
              onLoadMore={() => {
                return fetchList({
                  internal_store_id: storeId,
                  voucher_status: status,
                  searchKeyword: searchKeyword || undefined,
                  keywordType: searchKeyword ? searchKeywordType : undefined,
                })
                  .then(() => {
                    setHasMore(false);
                  })
                  .catch((err) => {
                    message.error(t('scene.chatWorkspace.couponTab.feedback.fetchFailed'));

                    return Promise.reject(err);
                  });
              }}
            >
              {(loadEle) => {
                return (
                  <List ref={wrapperRef}>
                    {vList.map((v) => {
                      const { data: voucher } = v;
                      const { internalVoucherId: id } = voucher;
                      return (
                        <li key={id}>
                          <VoucherComponent
                            status={voucher.voucherStatus}
                            voucher={voucher}
                            conversation={conversation}
                            onDelete={() => {
                              fetchList({
                                internal_store_id: storeId,
                                voucher_status: status,
                                searchKeyword: searchKeyword || undefined,
                                keywordType: searchKeyword ? searchKeywordType : undefined,
                              })
                                .then(() => {
                                  setHasMore(false);
                                })
                                .catch((err) => {
                                  message.error(
                                    t('scene.chatWorkspace.couponTab.feedback.fetchFailed'),
                                  );

                                  return Promise.reject(err);
                                });
                            }}
                            onUpdate={updateVoucher}
                            onEdit={editVoucher}
                            onCopyAndCreate={copyAndCreateVoucher}
                            getHeight={(height) =>
                              height && (idHeightMapRef.current[id] = height + 8)
                            }
                          />
                        </li>
                      );
                    })}
                    <li>{loadEle}</li>
                  </List>
                );
              }}
            </LoadMore>
          </div>
        )}
      </Content>
      <VoucherDrawer
        ref={voucherRef}
        visible={drawerVisible}
        conversation={conversation}
        title={voucherDrawerTitle}
        confirmText={voucherDrawerConfirmText}
        onCancel={(status) => {
          if (status) {
            setStatus(status);
          }
          setDrawerVisible(false);
          setVoucherDetailShow(false);
        }}
        afterSubmit={() => {
          setList([]);
          setHasMore(true);
        }}
      />
    </Container>
  );
}

export default memo(Voucher);
